import * as Sentry from "@sentry/nuxt";
import type { UserDetail } from "mws-ts-v2-sdk";
import type { NuxtApp } from "nuxt/app";
import { getModule } from "vuex-module-decorators";

import { store } from "~/store";
import PlatformModule from "~/store/PlatformModule";

type TrackingHeaders = {
  fbp?: string;
  fbc?: string;
  ttclid?: string;
  rdtUuid?: string;
  rdtcid?: string;
  googleClickId?: string;
  gbraid?: string;
  wbraid?: string;
  ga4ClientId?: string;
  ga4SessionId?: string;
  ga4SessionNumber?: string;
};

export function getAnalyticsHeaders(nuxtApp: NuxtApp) {
  const properties: TrackingHeaders = {
    fbp: getCookie(nuxtApp, "_fbp"),
    fbc: getCookie(nuxtApp, "_fbc"),
    ttclid: getCookie(nuxtApp, "ttclid"),
    rdtUuid: getCookie(nuxtApp, "_rdt_uuid"),
    rdtcid: getTrackerFromLocalStorage("rdt_cid"),
    gbraid: getTrackerFromLocalStorage("gbraid"),
    wbraid: getTrackerFromLocalStorage("wbraid"),
    ga4ClientId: getGa4ClientId(nuxtApp),
    googleClickId: getTrackerFromLocalStorage("gclid"),
  };

  try {
    const platformStore = getModule(PlatformModule, store);
    const sessionIds = getGa4SessionIds(
      nuxtApp,
      platformStore.getPlatformConfiguration?.tracking?.ga4MeasurementId,
    );

    if (sessionIds.length > 1) {
      const sessionId = sessionIds.shift();
      const sessionNumber = sessionIds.shift();

      if (sessionId) {
        properties.ga4SessionId = sessionId;
      }

      if (sessionNumber) {
        properties.ga4SessionNumber = sessionNumber;
      }
    }
  } catch {
    // means this function was called before the segment or platform were ready
    // this usually happens in the auth calls from the initial render
    // since they run before the platform and segment plugins
  }

  const {
    ga4SessionId,
    ga4SessionNumber,
    ga4ClientId,
    googleClickId,
    ...rest
  } = properties;
  const kebabCaseHeaders: Record<string, string | undefined> = {
    ...rest,
    "ga4-client-id": ga4ClientId,
    "google-click-id": googleClickId,
    "ga4-session-id": ga4SessionId,
    "ga4-session-number": ga4SessionNumber,
    channel: "web",
  };

  return {
    analyticsContext: {
      context: {
        userAgent: import.meta.server ? undefined : navigator.userAgent,
        channel: "web",
      },
      properties,
    },
    headers: kebabCaseHeaders,
  };
}

/**
 * Identifies the user for analytics purposes. This is intended to be called after the user logs in.
 */
export async function identifyUserAnalytics(
  user: UserDetail,
  nuxtApp: NuxtApp,
) {
  // Identify the user in sentry
  Sentry.setUser({ email: user.email });

  const { analyticsContext } = getAnalyticsHeaders(nuxtApp);

  // Identify the user in segment
  const properties: Record<string, string | undefined> = {
    email: user.email,
    phone_number: user.phoneNumber,
    phone: user.phoneNumber?.replace("+", ""), // meta wants it without the '+' (aka 'normalized')

    ga4_client_id: analyticsContext.properties.ga4ClientId,
    google_click_id: analyticsContext.properties.googleClickId,
    fbp: analyticsContext.properties.fbp,
    fbc: analyticsContext.properties.fbc,
    ttclid: analyticsContext.properties.ttclid,
    gbraid: analyticsContext.properties.gbraid,
    wbraid: analyticsContext.properties.wbraid,
  };

  try {
    await nuxtApp.$segment.browser.value.identify(user.id, properties);
    const segmentUser = await nuxtApp.$segment.browser.value.user();
    // Identify the user in growthbook
    await nuxtApp.$growthbook?.identify({
      userId: user.id,
      anonymousId: segmentUser?.anonymousId(),
    });
  } catch {
    // means this function was called before the segment/growthbook plugins were ready
    // this usually happens in the auth calls from the initial render
    // since they run before the client plugins
  }
}
